
import VueTypedMixin from "vue-typed-mixins";
import FavoriteMixin from "@/mixins/favorite";
import { mapMutations, mapState } from "vuex";
import { unreadAnnouncementNumber, hasAccessToken, questionaireBtnClicked } from "@/mixins/common-functions";
import { logout } from "@/usecase/logout";
import { getLocalStorage } from "@/util/utils";
import { isWcagMode } from "@/util/wcag";
// components
import BoxButton from "@/components/top-view/box-button.vue";
import MenuItem from "@/components/menu-view/menu-item.vue";
import VButton from "@/components/common/button.vue";
import LayoutIosDummy from "@/layouts/layout-ios-dummy.vue";
import MaterialIcons from "../common/material-icons.vue";
import MapsFooter from "@/components/v-footer.vue";

export default VueTypedMixin(FavoriteMixin).extend({
  name: "component.menu-list",
  mixins: [FavoriteMixin],
  components: {
    MenuItem,
    VButton,
    LayoutIosDummy,
    BoxButton,
    MaterialIcons,
    MapsFooter,
  },
  props: {
    isMobile: Boolean,
    gpsButtonStatus: {
      type: String,
      default: "normal",
    },
    isPollBtnActive: Boolean,
    showPollBtnTitle: String,
  },
  data() {
    return {
      totalAnnouncementNumber: this.$store.state.data.announcementNumber as number,
      accountName: this.$i18n.t(`MapLeftPanel.login`) as string,
    };
  },
  computed: {
    ...mapState("auth", ["tokenEmail"]),

    isWcagMode(): boolean {
      return isWcagMode();
    },

    // map types
    showHinan(): string {
      return require("@/assets/images/top/top-hinan.svg");
    },
    showPoll(): string {
      return require("@/assets/images/top/top-polling.svg");
    },
    showNormalMap(): string {
      return require("@/assets/images/top/top-pin.svg");
    },
    showLogoutTitle(): boolean {
      return this.tokenEmail !== undefined;
    },
    unreadAnnouncementNumber(): number {
      return unreadAnnouncementNumber();
    },
  },
  watch: {
    tokenEmail(newEmail: string) {
      if (newEmail) {
        this.accountName = newEmail;
      } else {
        this.accountName = this.$i18n.t(`MapLeftPanel.login`) as string;
      }
    },
  },
  mounted() {
    this.accountName = this.setAccountName();
  },
  methods: {
    ...mapMutations("ui", ["showMenuPanel"]),
    ...mapMutations("auth", ["setTokenEmail"]),
    // map types
    searchCurrentBtnClicked() {
      console.log(`menu current-location-clicked`);
      this.$emit("current-location-clicked");
    },
    searchHinanjoBtnClicked() {
      console.log(`menu evacuation-center-clicked`);
      this.$emit("evacuation-center-clicked");
    },
    searchPollBtnClicked() {
      console.log(`menu polling-place-clicked`);
      this.$emit("polling-place-clicked");
    },
    headerLogoClicked() {
      this.showMenuPanel(false);
      this.$router.push("/").catch();
    },
    closeMenuPanel() {
      this.$emit("close-menu");
    },
    setAccountName(): string {
      const savedEmail: string | null = getLocalStorage("session/tokens/email");
      return savedEmail === null || savedEmail === "" ? this.accountName : savedEmail;
    },
    loginClicked() {
      if (!this.showLogoutTitle) this.$router.push(`/login?fp=${this.$route.path}`);
    },
    async logoutClicked() {
      await logout();
      // update UI
      this.setTokenEmail(undefined);
      this.accountName = this.$i18n.t(`MapLeftPanel.login`) as string;

      this.CLEAR_ALL_FAVORITE_ON_PLACES();
      this.$forceUpdate();
    },
    itemClicked(menuId: number) {
      switch (menuId) {
        case 1:
          this.showAnnouncementView();
          break;
        case 2:
          this.showTutorialView();
          break;
        case 3: {
          // GA, GTM tracking
          this.$gtag.event("click", {
            event_category: "Menu item: FAQ",
            event_label: "faq",
            value: 1,
          });

          this.$router.push("/faq");
          break;
        }
        case 8: {
          // GA, GTM tracking
          this.$gtag.event("click", {
            event_category: "Menu item: Favorite",
            event_label: "Favorite",
            value: 1,
          });

          if (hasAccessToken()) {
            this.$router.push("/favorite");
          } else {
            this.$store.commit("ui/setFavoritePrompt", true);
          }
          break;
        }
        case 9: {
          // GA, GTM tracking
          this.$gtag.event("click", {
            event_category: "Menu item: Language",
            event_label: "Language",
            value: 1,
          });
          this.$emit("set-language-prompt", true);
          break;
        }
        case 10: {
          // GA, GTM tracking
          this.$gtag.event("click", {
            event_category: "Menu item: Top",
            event_label: "Top page",
            value: 1,
          });
          this.$router.push("/").catch(() => {
            console.warn("catch push /");
          });
          this.showMenuPanel(false);
          break;
        }
        case 11: {
          // GA, GTM tracking
          this.$gtag.event("click", {
            event_category: "Menu item: Color contrast",
            event_label: "WCAG selection page",
            value: 1,
          });
          this.$router.push("/wcag-selection").catch(() => {
            console.warn("catch push /wcag-selection");
          });
          this.showMenuPanel(true);
          break;
        }
        default:
          return;
      }
    },
    showTutorialView() {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Menu item: Tutorial",
        event_label: "Tutorial",
        value: 1,
      });

      this.$router.push("/tutorial");
    },
    showAnnouncementView(): void {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Menu item: Announcement",
        event_label: "Announcement",
        value: 1,
      });

      this.$store.commit("ui/setAnnouncementViewShown", true);
      this.$router.push("/announcement");
      this.increaseLayerOfWhiteBgView();
    },
    increaseLayerOfWhiteBgView() {
      this.$store.commit("ui/setZIndex", 3);
    },
    showTosPage(): void {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Menu item: Terms of Service",
        event_label: "Terms of Service",
        value: 1,
      });

      window.open("https://corp.vacan.com/guide/terms-maps-pages-users");
    },
    showPrivacyPage(): void {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Menu item: Privacy",
        event_label: "Privacy",
        value: 1,
      });

      window.open("https://corp.vacan.com/guide/policy");
    },
    showQuestionnaire(): void {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Menu item: Questionnaire",
        event_label: "Questionnaire",
        value: 1,
      });

      questionaireBtnClicked();
    },
  },
});
